import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { FileText, Plus, MoreVertical, Pencil, Trash2, Search, MapPin, CheckCircle, XCircle, AlertTriangle, Clock, Plane, PlayCircle, Map, Radar, ChevronDown, ChevronUp, Archive, Wrench } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import { MissionLocationMap } from "../components/MissionLocationMap";
import GeometryImportDialog from "../components/GeometryImportDialog";
import {
  buildFlightAreaPayload, circleRadiusLimitsFromSettings, classifyFlightArea, FLIGHT_AREA_KIND, flightAreaBlockerMessages,
  getCircleRadiusIssue, getCorridorWidthIssue,
  isFlightAreaLocked, isMissingIssue, localizedIssue, missionSaveBlockReason, useFlightAreaSettings,
  useFlightAreaValidation,
} from "../lib/flightArea";
import { getApiErrorMessage } from "../lib/apiErrors";
import { editReturnPath } from "../lib/missionEditNavigation";
import { legacyReasonText } from "../components/map/FlightAreaPanel";
import { formatIsoDateDmy } from "../lib/dateFormat";
import { MissionEnvironmentField } from "../components/mission/MissionEnvironmentField";
import { MissionAltitudeField } from "../components/mission/MissionAltitudeField";
import { ALTITUDE_MODE, useMissionAltitudeRecommendation } from "../lib/heightLimit";
import { fillTemplate } from "../lib/arcDisplay";


export const Missions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t, language, translate } = useLanguage();
  const [missions, setMissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("planning");
  const [showDialog, setShowDialog] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [uSpaceSectionOpen, setUSpaceSectionOpen] = useState(false);
  // The mission detail the open edit dialog was started from — closing the dialog (saved or not)
  // returns there (lib/missionEditNavigation.js). Null for a dialog opened from this list.
  const editReturnToRef = useRef(null);
  // The site name last filled in from the map (municipality of the take-off point or a search result):
  // a newer one replaces it, a name typed by the user is never overwritten. Cleared on every dialog open
  // and on typing into the field.
  const autoLocationRef = useRef(null);
  const [formData, setFormData] = useState({
    organization_id: "",
    pilot_id: "",
    aircraft_id: "",
    scenario_id: "",
    mission_date: "",
    location: "",
    latitude: null,
    longitude: null,
    environment_type: "rural",
    flight_mode: "VLOS",
    max_altitude_m: 50,
    etot: "",  // U-Space: Estimated Time of Take-Off
    eldt: "",  // U-Space: Estimated Landing Time
    mitigation_confirmation: false,
    notes: "",
    // The mission's single flight area (Polygon, LineString + corridor width, or a circle — Point + radius)
    // — see lib/flightArea.js.
    flight_area: null,
    corridor_width_m: null,
    circle_radius_m: null,
    flight_area_source: null  // { format, original_filename } when the area came from an imported file
  });
  // The rendered site name, for the map's address answers that arrive after a network round trip.
  const locationRef = useRef(formData.location);
  locationRef.current = formData.location;
  // Geometry import (edit of an existing mission only), map re-fit after an import, and the width
  // field highlight after an imported line.
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [mapFitKey, setMapFitKey] = useState(0);
  const [highlightWidth, setHighlightWidth] = useState(false);
  // A draw / edit / delete session is open on the map (MissionLocationMap reports it) — its geometry
  // is not in formData yet, so the main save is blocked until the user finishes or cancels it.
  const [mapSessionActive, setMapSessionActive] = useState(false);
  const flightAreaSettings = useFlightAreaSettings();
  // Tracks whether the user has manually touched the Flight Mode field this session — while false,
  // selecting a scenario auto-syncs flight_mode from the scenario's own operation_type (CheckMate SAIL
  // fix, 2026-09).
  const [flightModeTouched, setFlightModeTouched] = useState(false);

  // Phase X4: Maintenance conflict warning
  const [maintenanceWarning, setMaintenanceWarning] = useState(null);
  const maintenanceCheckTimeout = useRef(null);
  
  // Check maintenance overlap when aircraft + times change
  const checkMaintenanceOverlap = useCallback(async (aircraftId, etot, eldt) => {
    if (!aircraftId || !etot || !eldt) {
      setMaintenanceWarning(null);
      return;
    }
    
    try {
      const res = await api.get(`/aircraft/${aircraftId}/maintenance-check`, {
        params: {
          start: new Date(etot).toISOString(),
          end: new Date(eldt).toISOString()
        }
      });
      
      if (res.data.has_maintenance) {
        setMaintenanceWarning(res.data);
      } else {
        setMaintenanceWarning(null);
      }
    } catch (err) {
      console.error('Maintenance check failed:', err);
      setMaintenanceWarning(null);
    }
  }, []);
  
  // Debounced maintenance check
  useEffect(() => {
    if (maintenanceCheckTimeout.current) {
      clearTimeout(maintenanceCheckTimeout.current);
    }
    
    maintenanceCheckTimeout.current = setTimeout(() => {
      checkMaintenanceOverlap(formData.aircraft_id, formData.etot, formData.eldt);
    }, 500);
    
    return () => {
      if (maintenanceCheckTimeout.current) {
        clearTimeout(maintenanceCheckTimeout.current);
      }
    };
  }, [formData.aircraft_id, formData.etot, formData.eldt, checkMaintenanceOverlap]);

  // ── Take-off point + flight area (mission-map unification, PR6) ─────────────────────────────
  // Both are required on create and edit (the server will enforce it later, in the strict PR); the
  // dialog validates live against POST /missions/flight-area/validate and the server re-checks on save.
  const fa = t.flightArea || {};
  const hasTakeoffPoint = Number.isFinite(formData.latitude) && Number.isFinite(formData.longitude);
  const flightAreaClass = classifyFlightArea(formData.flight_area);
  const flightAreaValidation = useFlightAreaValidation({
    flightArea: formData.flight_area,
    corridorWidthM: formData.corridor_width_m,
    circleRadiusM: formData.circle_radius_m,
    latitude: hasTakeoffPoint ? formData.latitude : null,
    longitude: hasTakeoffPoint ? formData.longitude : null,
    enabled: showDialog,
  });

  // A completed mission's area and take-off point are locked — unless the STORED mission lacks them,
  // has an area the editor cannot use, or the server rejects its stored spatial state (legacy data,
  // a point moved by PATCH, stricter settings) — so it can still be fixed and saved. The exception is
  // judged on the stored values only (their own validation below), never on the draft being edited.
  const isCompletedEdit = editingMission?.lifecycle_status === "completed";
  const storedFlightAreaValidation = useFlightAreaValidation({
    flightArea: editingMission?.flight_area,
    corridorWidthM: editingMission?.corridor_width_m,
    circleRadiusM: editingMission?.circle_radius_m,
    latitude: Number.isFinite(editingMission?.latitude) ? editingMission.latitude : null,
    longitude: Number.isFinite(editingMission?.longitude) ? editingMission.longitude : null,
    enabled: showDialog && isCompletedEdit,
  });
  const mapLocked = isFlightAreaLocked({
    mission: editingMission,
    settings: flightAreaSettings,
    validationResult: storedFlightAreaValidation.result,
  });

  // Recommended max. altitude from the ŘLP height grid under the flight area: filled in after each
  // change of the area, corridor width or circle radius by the user, a stored or typed value is kept otherwise.
  const altitude = useMissionAltitudeRecommendation({
    flightArea: formData.flight_area,
    corridorWidthM: formData.corridor_width_m,
    circleRadiusM: formData.circle_radius_m,
    missionDate: formData.mission_date,
    enabled: showDialog,
    value: formData.max_altitude_m,
    locked: mapLocked,
    onValueChange: (value) => setFormData((prev) => ({ ...prev, max_altitude_m: value })),
    onOverwrite: (value) => toast.info(fillTemplate(t.flightOps?.altitudeGrid?.overwritten, { value })),
  });

  // Missing items ("Missing before saving: …") are kept apart from invalid ones (a self-crossing
  // polygon, a take-off point too far away) — those are not "missing".
  const flightAreaBlockerGroups = (() => {
    const missing = [];
    const invalid = [];
    if (!hasTakeoffPoint) missing.push(fa.takeoffRequired);
    if (flightAreaClass.kind === FLIGHT_AREA_KIND.NONE) {
      missing.push(fa.areaRequired);
    } else if (flightAreaClass.kind === FLIGHT_AREA_KIND.INVALID) {
      invalid.push(translate("flightArea.legacyInvalidArea", { reason: legacyReasonText(flightAreaClass.reason, fa) }));
    } else {
      const widthIssue = flightAreaClass.kind === FLIGHT_AREA_KIND.LINE
        ? getCorridorWidthIssue(formData.corridor_width_m, flightAreaSettings) : null;
      if (widthIssue === "required") missing.push(fa.corridorWidthRequired);
      if (widthIssue === "range" && flightAreaSettings) {
        invalid.push(translate("flightArea.corridorWidthRange", {
          min: flightAreaSettings.corridor_width_min_m, max: flightAreaSettings.corridor_width_max_m,
        }));
      }
      const radiusLimits = circleRadiusLimitsFromSettings(flightAreaSettings);
      const radiusIssue = flightAreaClass.kind === FLIGHT_AREA_KIND.CIRCLE
        ? getCircleRadiusIssue(formData.circle_radius_m, radiusLimits) : null;
      if (radiusIssue === "required") missing.push(fa.circleRadiusRequired);
      if (radiusIssue === "range" && radiusLimits) invalid.push(translate("flightArea.circleRadiusRange", radiusLimits));
      const result = flightAreaValidation.result;
      if (result && !result.valid) {
        (result.errors || [])
          .filter((e) => !(widthIssue && e.code?.startsWith("corridor_width")))
          .filter((e) => !(radiusIssue && e.code?.startsWith("circle_radius")))
          .forEach((e) => (isMissingIssue(e) ? missing : invalid).push(localizedIssue(e, language)));
      }
    }
    const unique = (items) => [...new Set(items.filter(Boolean))];
    return { missing: unique(missing), invalid: unique(invalid) };
  })();
  const flightAreaBlockers = [...flightAreaBlockerGroups.missing, ...flightAreaBlockerGroups.invalid];
  const flightAreaBlockerTexts = flightAreaBlockerMessages(flightAreaBlockerGroups, translate);
  const saveBlockReason = missionSaveBlockReason({ mapSessionActive, flightAreaBlockers });

  const handleFlightAreaChange = (geometry) => {
    setHighlightWidth(false);
    altitude.areaChanged();
    setFormData((prev) => ({
      ...prev,
      flight_area: geometry,
      // A drawn/edited geometry is no longer the imported file's.
      flight_area_source: null,
      corridor_width_m: geometry?.type === "LineString" ? prev.corridor_width_m : null,
      // A drawn circle's radius comes separately (onCircleRadiusChange); any other shape has none.
      circle_radius_m: geometry?.type === "Point" ? prev.circle_radius_m : null,
    }));
  };

  // A site name from the map (a municipality, or the first part of a search result) fills in an empty
  // field or replaces the previous one from the map; a name typed by the user stays.
  const handleAddressChange = (address) => {
    const name = address.split(",")[0].trim();
    const current = locationRef.current || "";
    if (!name || (current.trim() && current !== autoLocationRef.current)) return;
    autoLocationRef.current = name;
    setFormData((prev) => ({ ...prev, location: name }));
  };

  const handleGeometrySelected = (geometry, source) => {
    // The import button is disabled during a map session; the session's layer would win over the import.
    if (mapSessionActive) {
      toast.error(fa.finishMapSession);
      return;
    }
    if (formData.flight_area && !window.confirm(fa.replaceConfirm)) return;
    const isLine = geometry.type === "LineString";
    setFormData((prev) => ({
      ...prev,
      flight_area: geometry,
      flight_area_source: source,
      corridor_width_m: isLine
        ? (prev.corridor_width_m ?? flightAreaSettings?.corridor_width_default_m ?? null)
        : null,
      // An import never brings a circle.
      circle_radius_m: null,
    }));
    altitude.areaChanged();
    setHighlightWidth(isLine);
    setMapFitKey((k) => k + 1);
    toast.success(fa.importApplied);
  };

  const fetchData = useCallback(async () => {
    try {
      const [missionsRes, orgsRes, pilotsRes, aircraftRes, scenariosRes] = await Promise.all([
        api.get(`/missions`),
        api.get(`/organizations`),
        api.get(`/pilots`),
        api.get(`/aircraft`),
        api.get(`/scenarios`)
      ]);
      setMissions(missionsRes.data);
      setOrganizations(orgsRes.data);
      setPilots(pilotsRes.data);
      setAircraft(aircraftRes.data);
      setScenarios(scenariosRes.data);
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enter in a form field submits too — an open map session must not save the old geometry.
    if (saveBlockReason === "map_session") {
      toast.error(fa.finishMapSession);
      return;
    }
    
    if (!formData.pilot_id) {
      toast.error(t.validation?.selectPilot || "Please select a pilot");
      return;
    }
    if (!formData.aircraft_id) {
      toast.error(t.validation?.selectAircraft || "Please select an aircraft");
      return;
    }
    if (!formData.mission_date) {
      toast.error(t.validation?.selectDate || "Please select a mission date");
      return;
    }
    if (!formData.location.trim()) {
      toast.error(t.validation?.enterLocation || "Please enter a location");
      return;
    }
    const maxAltitude = formData.max_altitude_m;
    if (typeof maxAltitude !== "number" || !(maxAltitude >= 1 && maxAltitude <= 500)) {
      toast.error(t.validation?.maxAltitudeRange || "Enter a max. flight altitude between 1 and 500 m AGL");
      return;
    }
    if (!formData.mitigation_confirmation) {
      toast.error(t.flightOps?.confirmMitigationRequired || "Please confirm that all risk mitigation measures were followed");
      return;
    }
    if (flightAreaBlockers.length > 0) {
      toast.error(flightAreaBlockerTexts.join(" "));
      return;
    }

    // One request: the flight area, its corridor width and the take-off point go with the mission
    // (no separate PUT /flight-area; flight_plan_polygon is no longer sent).
    const { flight_area, corridor_width_m, circle_radius_m, flight_area_source, ...missionFields } = formData;
    const payload = {
      ...missionFields,
      organization_id: scopedOrganizationId,
      scenario_id: (formData.scenario_id && formData.scenario_id !== "__none__") ? formData.scenario_id : null,
      notes: formData.notes || null,
      // Convert empty strings to null for U-Space datetime fields
      etot: formData.etot || null,
      eldt: formData.eldt || null,
      ...buildFlightAreaPayload({ flight_area, corridor_width_m, circle_radius_m, flight_area_source }),
    };

    try {
      if (editingMission) {
        await api.put(`/missions/${editingMission.id}`, payload);
        toast.success(t.messages?.missionUpdated || "Mission updated successfully");
      } else {
        await api.post(`/missions`, payload);
        toast.success(t.messages?.missionLogged || "Mission logged successfully");
      }
      const returning = closeDialog();
      setEditingMission(null);
      resetForm();
      if (!returning) fetchData();
    } catch (error) {
      // Flight-area 400s carry { detail: { error, message_cs, message_en } }.
      toast.error(getApiErrorMessage(error, language,
        t.flightOps?.saveMissionError || "Failed to save mission. Please check all fields."));
    }
  };

  const handleDelete = async (mission) => {
    if (!window.confirm(`Delete mission from ${mission.mission_date}?`)) return;
    try {
      await api.delete(`/missions/${mission.id}`);
      toast.success(t.messages?.missionDeleted || "Mission deleted");
      fetchData();
    } catch (error) {
      toast.error(t.messages?.deleteError || "Failed to delete mission");
    }
  };

  // Closes the mission dialog; one opened from a mission detail returns to it (replacing this list's
  // history entry, so back / reload never reopen the dialog). Returns whether it navigated away.
  const closeDialog = () => {
    setShowDialog(false);
    const returnTo = editReturnToRef.current;
    editReturnToRef.current = null;
    if (!returnTo) return false;
    navigate(returnTo, { replace: true });
    return true;
  };

  const handleEdit = (mission) => {
    editReturnToRef.current = null;
    autoLocationRef.current = null;
    setEditingMission(mission);
    setFormData({
      organization_id: mission.organization_id,
      pilot_id: mission.pilot_id,
      aircraft_id: mission.aircraft_id,
      scenario_id: mission.scenario_id || "",
      mission_date: mission.mission_date?.split("T")[0] || "",
      location: mission.location,
      latitude: mission.latitude ?? null,
      longitude: mission.longitude ?? null,
      environment_type: mission.environment_type || "rural",
      flight_mode: mission.flight_mode || "VLOS",
      // A mission without an altitude stays empty, so saving asks for one instead of storing an invented value.
      max_altitude_m: mission.max_altitude_m ?? "",
      etot: mission.etot ? mission.etot.slice(0, 16) : "",
      eldt: mission.eldt ? mission.eldt.slice(0, 16) : "",
      mitigation_confirmation: mission.mitigation_confirmation,
      notes: mission.notes || "",
      // flight_area is the only area source (flight_plan_polygon is neither read nor sent any more).
      flight_area: mission.flight_area || null,
      corridor_width_m: mission.corridor_width_m ?? null,
      circle_radius_m: mission.circle_radius_m ?? null,
      flight_area_source: null
    });
    altitude.reset(ALTITUDE_MODE.STORED);
    setHighlightWidth(false);
    setShowImportDialog(false);
    setMapFitKey((k) => k + 1);
    // Reset per dialog-open, same as resetForm() — if the user then picks a different scenario during
    // this edit, flight_mode auto-syncs from it once, same as on create, until they touch the field
    // directly.
    setFlightModeTouched(false);
    setShowDialog(true);
  };

  // Open the edit dialog for a mission requested via navigation state (e.g. the "Edit" button on
  // MissionDetail.js). Separate, working mechanism from the "Fix Issues" button's `?edit=` query
  // param, which is left as-is pending a customer question (see CLAUDE.md).
  useEffect(() => {
    const editId = location.state?.editMissionId;
    if (editId && missions.length > 0) {
      const target = missions.find((m) => m.id === editId);
      if (target) {
        handleEdit(target);
        editReturnToRef.current = editReturnPath(location.state);
        const status = getLifecycleStatus(target);
        setActiveTab(status === "draft" ? "planning" : status);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missions, location.state]);

  const resetForm = () => {
    const today = new Date().toISOString().split("T")[0];
    autoLocationRef.current = null;
    setFormData({
      organization_id: scopedOrganizationId || "",
      pilot_id: "",
      aircraft_id: "",
      scenario_id: "",
      mission_date: today,
      location: "",
      latitude: null,
      longitude: null,
      environment_type: "rural",
      flight_mode: "VLOS",
      max_altitude_m: 50,
      etot: "",
      eldt: "",
      mitigation_confirmation: false,
      notes: "",
      flight_area: null,
      corridor_width_m: null,
      circle_radius_m: null,
      flight_area_source: null
    });
    altitude.reset(null);
    setFlightModeTouched(false);
    setMaintenanceWarning(null);
    setHighlightWidth(false);
    setShowImportDialog(false);
    setMapFitKey((k) => k + 1);
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getPilotName = (pilotId) => {
    const pilot = pilots.find(p => p.id === pilotId);
    return pilot?.name || "-";
  };

  const getAircraftName = (acId) => {
    const ac = aircraft.find(a => a.id === acId);
    return ac ? ac.model : "-";
  };

  const getScenarioName = (scId) => {
    const sc = scenarios.find(s => s.id === scId);
    return sc?.scenario_name || "-";
  };

  const filteredMissions = missions.filter(m =>
    m.location.toLowerCase().includes(search.toLowerCase()) ||
    getPilotName(m.pilot_id).toLowerCase().includes(search.toLowerCase()) ||
    m.mission_id?.toLowerCase().includes(search.toLowerCase())
  );

  const getLifecycleStatus = (mission) => mission.lifecycle_status || "draft";
  
  const missionsByTab = {
    planning: filteredMissions.filter(m => ["draft"].includes(getLifecycleStatus(m))),
    ready: filteredMissions.filter(m => getLifecycleStatus(m) === "ready"),
    active: filteredMissions.filter(m => getLifecycleStatus(m) === "active"),
    completed: filteredMissions.filter(m => getLifecycleStatus(m) === "completed")
  };

  const tabMissions = missionsByTab[activeTab] || filteredMissions;

  const nonCompliantCount = missions.filter(m => !m.compliance_flag && getLifecycleStatus(m) !== "completed").length;
  const incidentCount = missions.filter(m => m.incident_flag).length;

  const getLifecycleBadge = (status) => {
    const badges = {
      draft: <Badge variant="secondary">{t.lifecycle?.draft || "Draft"}</Badge>,
      ready: <Badge variant="success">{t.lifecycle?.ready || "Ready"}</Badge>,
      active: <Badge variant="active">{t.lifecycle?.active || "Active"}</Badge>,
      completed: <Badge variant="default">{t.lifecycle?.completed || "Completed"}</Badge>
    };
    return badges[status] || badges.draft;
  };

  // Readiness status badge (replaces old compliance_flag badge)
  const getReadinessBadge = (status) => {
    const badges = {
      draft: (
        <Badge variant="secondary" className="whitespace-nowrap text-xs">
          {t.missionStatus?.draft || "Draft"}
        </Badge>
      ),
      in_progress: (
        <Badge className="whitespace-nowrap text-xs bg-blue-500/15 text-blue-600 border-blue-500/30">
          {t.missionStatus?.inProgress || "In Progress"}
        </Badge>
      ),
      ready: (
        <Badge variant="success" className="whitespace-nowrap text-xs">
          {t.missionStatus?.ready || "Ready"}
        </Badge>
      ),
      not_ready: (
        <Badge variant="destructive" className="whitespace-nowrap text-xs">
          {t.missionStatus?.notReady || "Not Ready"}
        </Badge>
      )
    };
    return badges[status] || badges.draft;
  };

  const filteredPilots = pilots.filter(p => 
    !formData.organization_id || p.organization_id === formData.organization_id || 
    p.organization_id === scopedOrganizationId
  );
  const filteredAircraft = aircraft.filter(a => 
    !formData.organization_id || a.organization_id === formData.organization_id ||
    a.organization_id === scopedOrganizationId
  );
  const filteredScenarios = scenarios.filter(s => 
    !formData.organization_id || s.organization_id === formData.organization_id ||
    s.organization_id === scopedOrganizationId
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="missions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">
            {t.flightOps.missions}
          </h1>
          <p className="mt-1" className="text-slate-600">{t.flightOps.title}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingMission(null); editReturnToRef.current = null; setShowDialog(true); }}
          className="gap-2"
          data-testid="add-mission-btn"
        >
          <Plus className="h-4 w-4" /> {t.flightOps.logMission}
        </Button>
      </div>

      {/* Alerts */}
      {(nonCompliantCount > 0 || incidentCount > 0) && (
        <div className="flex gap-4">
          {nonCompliantCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: 'rgba(235, 87, 87, 0.1)', border: '1px solid rgba(235, 87, 87, 0.3)' }}>
              <XCircle className="h-4 w-4" style={{ color: 'var(--status-critical)' }} />
              <span className="text-sm" style={{ color: 'var(--status-critical)' }}>{nonCompliantCount} {t.missionStatus.nonCompliant.toLowerCase()}</span>
            </div>
          )}
          {incidentCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: 'rgba(242, 201, 76, 0.1)', border: '1px solid rgba(242, 201, 76, 0.3)' }}>
              <AlertTriangle className="h-4 w-4" style={{ color: 'var(--status-warning)' }} />
              <span className="text-sm" style={{ color: 'var(--status-warning)' }}>{incidentCount} {t.subNav.incidents.toLowerCase()}</span>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--text-muted)' }} />
        <Input
          placeholder={`${t.actions.search}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          style={{ 
            background: 'var(--input-bg)',
            borderColor: 'var(--input-border)',
            color: 'var(--text-primary)'
          }}
          data-testid="search-missions"
        />
      </div>

      {/* Lifecycle Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-4 h-auto gap-1 p-1" style={{ background: 'var(--bg-elevated)' }}>
          <TabsTrigger 
            value="planning" 
            className="gap-1.5 px-2 py-2 text-xs lg:text-sm data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-[var(--text-inverse)]"
            data-testid="tab-planning"
          >
            <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            <span className="hidden sm:inline">{t.lifecycle?.planning || "Planning"}</span>
            <span className="sm:hidden">Plan</span>
            <Badge variant="secondary" className="ml-0.5 text-xs">{missionsByTab.planning.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="ready" 
            className="gap-1.5 px-2 py-2 text-xs lg:text-sm data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-[var(--text-inverse)]"
            data-testid="tab-ready"
          >
            <CheckCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            {t.lifecycle?.ready || "Ready"}
            <Badge variant="secondary" className="ml-0.5 text-xs">{missionsByTab.ready.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="active" 
            className="gap-1.5 px-2 py-2 text-xs lg:text-sm data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-[var(--text-inverse)]"
            data-testid="tab-active"
          >
            <PlayCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            <span className="hidden sm:inline">{t.lifecycle?.activeToday || "Active (Today)"}</span>
            <span className="sm:hidden">Active</span>
            <Badge variant="secondary" className="ml-0.5 text-xs">{missionsByTab.active.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="completed" 
            className="gap-1.5 px-2 py-2 text-xs lg:text-sm data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-[var(--text-inverse)]"
            data-testid="tab-completed"
          >
            <CheckCircle className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            <span className="hidden sm:inline">{t.lifecycle?.completed || "Completed"}</span>
            <span className="sm:hidden">Done</span>
            <Badge variant="secondary" className="ml-0.5 text-xs">{missionsByTab.completed.length}</Badge>
          </TabsTrigger>
        </TabsList>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent" style={{ borderColor: 'var(--border-default)' }}>
                  <TableHead className="whitespace-nowrap w-[130px]" style={{ color: 'var(--text-muted)' }}>{t.flightOps.missionId}</TableHead>
                  <TableHead className="whitespace-nowrap w-[90px]" style={{ color: 'var(--text-muted)' }}>{t.common.date}</TableHead>
                  {user?.role === "platform_admin" && <TableHead className="whitespace-nowrap hidden xl:table-cell" style={{ color: 'var(--text-muted)' }}>{t.common.organization}</TableHead>}
                  <TableHead className="whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{t.flightOps.location}</TableHead>
                  <TableHead className="whitespace-nowrap hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>{t.flightOps.pilot}</TableHead>
                  <TableHead className="whitespace-nowrap hidden xl:table-cell" style={{ color: 'var(--text-muted)' }}>{t.flightOps.aircraft}</TableHead>
                  <TableHead className="whitespace-nowrap w-[80px]" style={{ color: 'var(--text-muted)' }}>{t.lifecycle?.status || "Status"}</TableHead>
                  <TableHead className="whitespace-nowrap w-[100px]" style={{ color: 'var(--text-muted)' }}>{t.flightOps.compliance}</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--brand-accent)', borderTopColor: 'transparent' }}></div>
                  </TableCell>
                </TableRow>
              ) : tabMissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                    <p style={{ color: 'var(--text-muted)' }}>{t.common.noData}</p>
                  </TableCell>
                </TableRow>
              ) : (
                tabMissions.map((mission) => (
                  <TableRow 
                    key={mission.id} 
                    data-testid={`mission-row-${mission.id}`} 
                    className="group cursor-pointer transition-colors"
                    style={{ borderColor: 'var(--border-default)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-elevated)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    onClick={(e) => {
                      // Don't navigate if clicking on action menu button or dropdown
                      if (e.target.closest('[data-radix-collection-item]') || e.target.closest('button')) return;
                      navigate(`/missions/${mission.id}`);
                    }}
                  >
                    <TableCell className="py-2">
                      <span
                        className="font-mono text-xs lg:text-sm hover:underline whitespace-nowrap"
                        style={{ color: 'var(--brand-accent)' }}
                        data-testid={`mission-link-${mission.id}`}
                      >
                        {mission.mission_id || "N/A"}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap text-xs lg:text-sm py-2" className="text-slate-900">{formatIsoDateDmy(mission.mission_date)}</TableCell>
                    {user?.role === "platform_admin" && (
                      <TableCell className="text-xs max-w-[100px] truncate hidden xl:table-cell py-2" style={{ color: 'var(--text-muted)' }}>{getOrgName(mission.organization_id)}</TableCell>
                    )}
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1 max-w-[150px] lg:max-w-[180px]">
                        <MapPin className="h-3 w-3 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
                        <span className="text-xs lg:text-sm truncate" className="text-slate-600">{mission.location}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs lg:text-sm whitespace-nowrap hidden lg:table-cell py-2" className="text-slate-600">{getPilotName(mission.pilot_id)}</TableCell>
                    <TableCell className="text-xs max-w-[100px] truncate hidden xl:table-cell py-2" style={{ color: 'var(--text-muted)' }}>{getAircraftName(mission.aircraft_id)}</TableCell>
                    <TableCell className="py-2">
                      {getLifecycleBadge(getLifecycleStatus(mission))}
                    </TableCell>
                    <TableCell className="py-2">
                      {/* Show "Archived" for completed missions, otherwise show readiness status */}
                      {getLifecycleStatus(mission) === "completed" ? (
                        <Badge className="whitespace-nowrap text-xs bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          <Archive className="h-3 w-3 mr-1" />
                          {t.missionStatus?.archived || "Archived"}
                        </Badge>
                      ) : (
                        <>
                          {getReadinessBadge(mission.readiness_status || "draft")}
                          {mission.incident_flag && (
                            <Badge variant="warning" className="ml-1 whitespace-nowrap text-xs hidden sm:inline-flex">{t.subNav.incidents}</Badge>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`mission-actions-${mission.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border-slate-200 shadow-lg">
                          <DropdownMenuItem onClick={() => handleEdit(mission)} className="text-slate-900">
                            <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(mission)} style={{ color: 'var(--status-critical)' }}>
                            <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
      </Tabs>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => (open ? setShowDialog(true) : closeDialog())}>
        <DialogContent className="w-[95vw] sm:max-w-[1440px] max-h-[90vh] flex flex-col bg-white border-slate-200">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-slate-900">{editingMission ? t.dialogs.editMission : t.dialogs.addMission}</DialogTitle>
          </DialogHeader>
          {/* The form and the scroll container both clip their overflow, so each gets -ml-1 pl-1: the focus ring of
              the fields fits into the padding while the fields, banner and footer stay aligned with the title. */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden -ml-1 pl-1" data-testid="mission-form">
            <div className="flex-1 overflow-y-auto space-y-4 -ml-1 pl-1 pt-1 pr-2 pb-4">
            {/* Two-column layout on lg+: form fields left, large map right (map first on small screens) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
            {user?.role === "platform_admin" && (
              <div className="space-y-2">
                <Label className="text-slate-600">{t.common.organization} *</Label>
                <Select
                  value={formData.organization_id}
                  disabled
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value, pilot_id: "", aircraft_id: "", scenario_id: "" })}
                >
                  <SelectTrigger data-testid="mission-organization" className="bg-white border-slate-300 text-slate-900">
                    <SelectValue placeholder={t.flightOps.selectOrganization} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 shadow-lg">
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id} className="text-slate-900">{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mission_date" className="text-slate-600">{t.flightOps.missionDate} *</Label>
                <DateInput
                  id="mission_date"
                  value={formData.mission_date}
                  onChange={(missionDate) => setFormData((prev) => ({ ...prev, mission_date: missionDate }))}
                  language={language}
                  required
                  data-testid="mission-date"
                  className="bg-white border-slate-300 text-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location" className="text-slate-600">{t.flightOps.location} *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => {
                    // Typed by the user, even when it equals the name from the map.
                    autoLocationRef.current = null;
                    setFormData({ ...formData, location: e.target.value });
                  }}
                  required
                  placeholder={t.placeholders?.locationExample || "e.g., Wind Farm Alpha"}
                  data-testid="mission-location"
                  className="bg-white border-slate-300 text-slate-900"
                />
                <p className="text-xs text-slate-500">{t.flightOps?.locationHelper || "Brief description of the site"}</p>
              </div>
            </div>

            {/* Environment Type — automatic from the ŘLP zones, manual, or unknown until the flight-area settings load */}
            <MissionEnvironmentField
              settings={flightAreaSettings}
              value={formData.environment_type}
              onChange={(value) => setFormData({ ...formData, environment_type: value })}
              storedEnvironmentType={editingMission?.environment_type}
              t={t}
            />

            {/* Flight Mode — previously only settable from inside the CheckMate panel; now also
                editable directly on the mission form, since it drives GRC/ARC/SAIL on both screens. */}
            <div className="space-y-2">
              <Label className="text-slate-600">{t.checkmate?.flightMode?.sectionTitle || "Flight Mode"}</Label>
              <Select
                value={formData.flight_mode}
                onValueChange={(value) => {
                  setFlightModeTouched(true);
                  setFormData({ ...formData, flight_mode: value });
                }}
              >
                <SelectTrigger data-testid="mission-flight-mode" className="bg-white border-slate-300 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 shadow-lg">
                  <SelectItem value="VLOS" className="text-slate-900">{t.checkmate?.flightMode?.vlos || "VLOS"}</SelectItem>
                  <SelectItem value="EVLOS" className="text-slate-900">{t.checkmate?.flightMode?.evlos || "EVLOS"}</SelectItem>
                  <SelectItem value="BVLOS" className="text-slate-900">{t.checkmate?.flightMode?.bvlos || "BVLOS"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Max Altitude */}
            <MissionAltitudeField
              value={formData.max_altitude_m}
              onChange={(value) => {
                altitude.typed();
                setFormData((prev) => ({ ...prev, max_altitude_m: value }));
              }}
              hint={altitude.hint}
              onApply={altitude.apply}
              t={t}
            />
            
            {/* U-Space Collapsible Section */}
            <div 
              className="rounded-lg border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800/50 overflow-hidden"
              data-testid="uspace-section"
            >
              <button
                type="button"
                onClick={() => setUSpaceSectionOpen(!uSpaceSectionOpen)}
                className="w-full flex items-center justify-between p-3 hover:bg-blue-100/50 dark:hover:bg-blue-900/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Radar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <div className="text-left">
                    <span className="font-medium text-sm" className="text-slate-900">
                      U-Space & Flight Plan
                    </span>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {t.uSpace?.sectionSubtitle || "Optional data for U-Space zone operations"}
                    </p>
                  </div>
                </div>
                {uSpaceSectionOpen ? (
                  <ChevronUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
              
              {uSpaceSectionOpen && (
                <div className="p-3 pt-0 space-y-4 border-t border-blue-200 dark:border-blue-800/50">
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label className="text-slate-600">{t.uSpace?.etot || "Planned Take-off Time (ETOT)"}</Label>
                      <Input
                        type="datetime-local"
                        value={formData.etot || ""}
                        onChange={(e) => setFormData({ ...formData, etot: e.target.value })}
                        data-testid="mission-etot"
                        className="bg-white border-slate-300 text-slate-900"
                      />
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.uSpace?.etotHelper || "Required for U-Space operations"}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600">{t.uSpace?.eldt || "Planned Landing Time (ELDT)"}</Label>
                      <Input
                        type="datetime-local"
                        value={formData.eldt || ""}
                        onChange={(e) => setFormData({ ...formData, eldt: e.target.value })}
                        data-testid="mission-eldt"
                        className="bg-white border-slate-300 text-slate-900"
                      />
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.uSpace?.eldtHelper || "Estimated Landing Time"}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">{t.flightOps.pilot} *</Label>
                <Select
                  value={formData.pilot_id}
                  onValueChange={(value) => setFormData({ ...formData, pilot_id: value })}
                >
                  <SelectTrigger data-testid="mission-pilot" className="bg-white border-slate-300 text-slate-900">
                    <SelectValue placeholder={t.flightOps.selectPilot} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 shadow-lg">
                    {filteredPilots.map((pilot) => (
                      <SelectItem key={pilot.id} value={pilot.id} className="text-slate-900">{pilot.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">{t.flightOps.aircraft} *</Label>
                <Select
                  value={formData.aircraft_id}
                  onValueChange={(value) => setFormData({ ...formData, aircraft_id: value })}
                >
                  <SelectTrigger data-testid="mission-aircraft" className="bg-white border-slate-300 text-slate-900">
                    <SelectValue placeholder={t.flightOps.selectAircraft} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 shadow-lg">
                    {filteredAircraft.map((ac) => (
                      <SelectItem key={ac.id} value={ac.id} className="text-slate-900">{ac.model} ({ac.serial_number})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">{t.flightOps.operationalScenario}</Label>
              <Select
                value={formData.scenario_id}
                onValueChange={(value) => {
                  // CheckMate SAIL fix (2026-09): picking a scenario only ever updated scenario_id,
                  // never flight_mode, even though the scenario's operation_type is what a new mission's
                  // Flight Mode should logically start as. Only auto-syncs while the user hasn't manually
                  // set Flight Mode themselves in this dialog session, and never for "no scenario".
                  if (!flightModeTouched && value !== "__none__") {
                    const selected = filteredScenarios.find((sc) => sc.id === value);
                    if (selected?.operation_type) {
                      setFormData({ ...formData, scenario_id: value, flight_mode: selected.operation_type });
                      return;
                    }
                  }
                  setFormData({ ...formData, scenario_id: value });
                }}
              >
                <SelectTrigger data-testid="mission-scenario" className="bg-white border-slate-300 text-slate-900">
                  <SelectValue placeholder={t.flightOps.selectScenario} />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 shadow-lg">
                  <SelectItem value="__none__" style={{ color: 'var(--text-muted)' }}>{t.none}</SelectItem>
                  {filteredScenarios.filter(s => s.active_status).map((sc) => (
                    <SelectItem key={sc.id} value={sc.id} className="text-slate-900">{sc.scenario_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-slate-600">{t.flightOps.notes}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t.flightOps.missionNotes}
                rows={2}
                data-testid="mission-notes"
                className="bg-white border-slate-300 text-slate-900"
              />
            </div>
            <div className="flex items-center space-x-2 pt-2 pb-2 px-3 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
              <Checkbox
                id="mitigation"
                checked={formData.mitigation_confirmation}
                onCheckedChange={(checked) => setFormData({ ...formData, mitigation_confirmation: checked })}
                data-testid="mission-mitigation"
              />
              <Label htmlFor="mitigation" className="text-sm cursor-pointer text-slate-600">
                {t.flightOps.confirmMitigation}
              </Label>
            </div>
            </div>

            {/* Location Map — right column on lg+, first on small screens; sticky so it stays
                in view while the form fields on the left scroll */}
            <div className="lg:col-span-3 order-first lg:order-none">
            <div className="space-y-2 lg:sticky lg:top-0">
              <Label className="flex items-center gap-2 text-slate-600">
                <Map className="h-4 w-4" />
                {fa.sectionTitle} *
              </Label>
              <p className="text-xs text-slate-500 -mt-1">{fa.sectionHelper}</p>
              <MissionLocationMap
                mode="edit"
                locked={mapLocked}
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationChange={(lat, lng) => setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }))}
                onAddressChange={handleAddressChange}
                flightArea={formData.flight_area}
                corridorWidthM={formData.corridor_width_m}
                onFlightAreaChange={handleFlightAreaChange}
                onCorridorWidthChange={(value) => {
                  setHighlightWidth(false);
                  altitude.areaChanged();
                  setFormData((prev) => ({ ...prev, corridor_width_m: value }));
                }}
                circleRadiusM={formData.circle_radius_m}
                onCircleRadiusChange={(value) => {
                  altitude.areaChanged();
                  setFormData((prev) => ({ ...prev, circle_radius_m: value }));
                }}
                settings={flightAreaSettings}
                validation={flightAreaValidation}
                fitKey={`${editingMission?.id || "new"}-${mapFitKey}`}
                height="max(420px, 58vh)"
                onImportClick={() => setShowImportDialog(true)}
                importAvailable={!!editingMission?.id}
                highlightWidth={highlightWidth}
                onMapSessionChange={setMapSessionActive}
                zonesDate={formData.mission_date}
              />
            </div>
            </div>
            </div>
            </div>
            
            {/* Phase X4: Maintenance Warning Banner */}
            {maintenanceWarning && (
              <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20" data-testid="maintenance-warning-banner">
                <Wrench className="h-5 w-5 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  <span className="font-medium">
                    {t.mission?.maintenance_conflict?.warning || 
                      (t.locale === 'cs' 
                        ? 'Vybraný dron je v servisu v tomto období.' 
                        : 'Selected aircraft is in maintenance during this period.')}
                  </span>
                  <div className="text-sm mt-1">
                    {maintenanceWarning.maintenance?.start_at?.slice(0, 16).replace('T', ' ')} - 
                    {maintenanceWarning.maintenance?.end_at?.slice(0, 16).replace('T', ' ')}
                    {maintenanceWarning.maintenance?.reason && (
                      <span className="ml-1">({maintenanceWarning.maintenance.reason})</span>
                    )}
                  </div>
                  <p className="text-sm mt-1 opacity-80">
                    {t.locale === 'cs' 
                      ? 'Změňte čas mise nebo vyberte jiného drona.' 
                      : 'Change mission time or select another aircraft.'}
                  </p>
                </AlertDescription>
              </Alert>
            )}
            
            <DialogFooter className="flex-shrink-0 pt-4 mt-2 border-t border-slate-200 sm:items-center">
              {saveBlockReason === "map_session" && (
                <p className="text-xs text-amber-700 sm:mr-auto sm:max-w-[60%]" data-testid="mission-save-map-session">
                  {fa.finishMapSession}
                </p>
              )}
              {saveBlockReason === "flight_area" && (
                <p className="text-xs text-red-700 sm:mr-auto sm:max-w-[60%]" data-testid="mission-save-blockers">
                  {flightAreaBlockerTexts.map((text) => <span key={text} className="block">{text}</span>)}
                </p>
              )}
              <Button type="button" variant="outline" onClick={closeDialog}>{t.actions.cancel}</Button>
              <Button
                type="submit"
                data-testid="mission-submit-btn"
                disabled={!!maintenanceWarning || saveBlockReason !== null}
                className={maintenanceWarning || saveBlockReason !== null ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {editingMission ? t.actions.update : t.flightOps.logMission}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Geometry import — edit of an existing mission only; the picked geometry goes into the form */}
      {showDialog && editingMission?.id && (
        <GeometryImportDialog
          isOpen={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          missionId={editingMission.id}
          onGeometrySelected={handleGeometrySelected}
          maxFileBytes={flightAreaSettings?.max_import_file_bytes}
        />
      )}
    </div>
  );
};
